import { useEffect, useState } from 'react';
import type { AiConfigDto, AiRequestLogDto } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { dateTime } from '@/lib/format';
import { Section, Table, Td, Th, Tr } from '@/components/page';
import { Alert, Button, Field, Input, Pill, Skeleton, cx } from '@/components/ui';
import { ToggleField } from '@/components/fields';
import { SettingsPage } from '@/pages/settings';

/**
 * The assistant (ADR-033).
 *
 * A base URL and a model name rather than a vendor picker: every model worth
 * using speaks the OpenAI chat format, so a store that will not send its
 * catalogue anywhere points this at a machine on its own network and nothing
 * else changes. The presets below are shortcuts, not a supported-vendor list.
 */
const PRESETS = [
  { label: 'OpenAI', baseUrl: 'https://api.openai.com/v1', model: 'gpt-4o-mini' },
  { label: 'Ollama (local)', baseUrl: 'http://localhost:11434/v1', model: 'llama3.1' },
  { label: 'LM Studio (local)', baseUrl: 'http://localhost:1234/v1', model: 'local-model' },
  { label: 'Groq', baseUrl: 'https://api.groq.com/openai/v1', model: 'llama-3.3-70b-versatile' },
];

export function AiPage() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);

  const [config, setConfig] = useState<AiConfigDto | null>(null);
  const [history, setHistory] = useState<AiRequestLogDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [baseUrl, setBaseUrl] = useState('');
  const [model, setModel] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [timeoutSeconds, setTimeoutSeconds] = useState(30);

  const load = async () => {
    try {
      const [current, log] = await Promise.all([
        withSession(() => adminApi.ai.config()),
        withSession(() => adminApi.ai.history({ limit: 25 })),
      ]);

      setConfig(current);
      setHistory(log.items);
      setBaseUrl(current.baseUrl);
      setModel(current.model);
      setTimeoutSeconds(Math.round(current.timeoutMs / 1000));
    } catch (cause) {
      setError(errorMessage(cause, 'Could not load the AI settings.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const act = async (run: () => Promise<unknown>, after?: string) => {
    setSaving(true);
    setError(null);
    setNotice(null);

    try {
      await run();
      if (after) setNotice(after);
      await load();
    } catch (cause) {
      setError(errorMessage(cause, 'Could not save.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !config) {
    return (
      <SettingsPage title="aiAssistant" subtitle="settingsAiHint">
        {loading ? <Skeleton className="h-64" /> : error ? <Alert>{error}</Alert> : null}
      </SettingsPage>
    );
  }

  const succeeded = history.filter((row) => row.succeeded).length;
  const tokens = history.reduce(
    (sum, row) => sum + (row.promptTokens ?? 0) + (row.completionTokens ?? 0),
    0,
  );

  return (
    <SettingsPage
      title="aiAssistant"
      subtitle="settingsAiHint"
      actions={
        <>
          <Button
            variant="secondary"
            disabled={saving || !config.enabled}
            onClick={() =>
              void act(async () => {
                const result = await withSession(() => adminApi.ai.test());
                setNotice(`${t('aiTestOk')} — ${result.model}: “${result.reply}”`);
              })
            }
          >
            {t('aiTest')}
          </Button>
          <Button
            disabled={saving}
            onClick={() => void act(() => adminApi.ai.update({ enabled: !config.enabled }))}
          >
            {config.enabled ? t('disable') : t('enable')}
          </Button>
        </>
      }
    >
      <p className="text-sm text-ink-muted">{t('aiSubtitle')}</p>

      {!config.enabled ? (
        <Alert tone="info">
          <Pill tone="neutral">{t('deactivated')}</Pill> {t('aiOffHint')}
        </Alert>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}
      {notice ? <Alert tone="success">{notice}</Alert> : null}

      <div className="grid items-start gap-5 lg:grid-cols-3">
        <Section title={t('aiModel')} subtitle={t('aiModelHint')} className="lg:col-span-2">
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((preset) => (
              <button
                key={preset.label}
                type="button"
                className="rounded-full border border-line px-3 py-1 text-xs text-ink-muted hover:border-brand-400 hover:text-ink"
                onClick={() => {
                  setBaseUrl(preset.baseUrl);
                  setModel(preset.model);
                }}
              >
                {preset.label}
              </button>
            ))}
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label={t('aiBaseUrl')} htmlFor="ai-url" hint={t('aiBaseUrlHint')}>
              <Input id="ai-url" dir="ltr" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
            </Field>

            <Field label={t('aiModelName')} htmlFor="ai-model" hint={t('aiModelNameHint')}>
              <Input id="ai-model" dir="ltr" value={model} onChange={(e) => setModel(e.target.value)} />
            </Field>

            <Field
              label={t('aiKeyLabel')}
              htmlFor="ai-key"
              /* Optional on purpose — a local model has no key, and demanding
                 one would lock out the deployment that most wants this. */
              hint={config.hasApiKey ? `${t('aiKeyStored')} ${config.apiKeyPreview}` : t('aiKeyOptional')}
            >
              <Input
                id="ai-key"
                dir="ltr"
                type="password"
                placeholder={config.hasApiKey ? '••••••••' : 'sk-…'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
              />
            </Field>

            <Field label={t('aiTimeout')} htmlFor="ai-timeout" hint={t('aiTimeoutHint')}>
              <Input
                id="ai-timeout"
                type="number"
                min={5}
                max={120}
                value={timeoutSeconds}
                onChange={(e) => setTimeoutSeconds(Number(e.target.value))}
              />
            </Field>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-line pt-4">
            <Button
              disabled={saving}
              onClick={() =>
                void act(
                  () =>
                    adminApi.ai.update({
                      baseUrl,
                      model,
                      timeoutMs: timeoutSeconds * 1000,
                      ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
                    }),
                  t('saved'),
                ).then(() => setApiKey(''))
              }
            >
              {saving ? t('saving') : t('save')}
            </Button>

            {config.hasApiKey ? (
              <Button
                variant="ghost"
                disabled={saving}
                onClick={() => void act(() => adminApi.ai.update({ apiKey: null }), t('saved'))}
              >
                {t('aiClearKey')}
              </Button>
            ) : null}
          </div>
        </Section>

        <Section title={t('aiUsage')} subtitle={t('aiUsageHint')}>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-ink-muted">{t('aiRecentCalls')}</dt>
              <dd data-numeric className="font-medium text-ink">
                {history.length}
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-ink-muted">{t('aiSucceeded')}</dt>
              <dd data-numeric className="font-medium text-ink">
                {succeeded}
                {history.length > succeeded ? (
                  <span className="ms-2 text-danger">
                    {history.length - succeeded} {t('aiFailed')}
                  </span>
                ) : null}
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-ink-muted">{t('aiTokens')}</dt>
              {/* Zero is normal, not a bug: a local model reports no usage. */}
              <dd data-numeric className="font-medium text-ink">
                {tokens || '—'}
              </dd>
            </div>
          </dl>
        </Section>
      </div>

      <Section title={t('aiTasks')} subtitle={t('aiTasksHint')} flush>
        <ul className="divide-y divide-line border-t border-line">
          {config.tasks.map((task) => (
            <li key={task.key} className="flex flex-wrap items-start justify-between gap-3 px-5 py-4">
              <div className="min-w-0 max-w-2xl">
                <p className="text-sm font-medium text-ink">{task.name}</p>
                <p className="mt-0.5 text-sm text-ink-muted">{task.summary}</p>
                <p className="mt-1 text-xs text-ink-faint">{task.surface}</p>
              </div>

              <div className="shrink-0">
                <ToggleField
                  label={task.enabled ? t('enabled') : t('deactivated')}
                  checked={task.enabled}
                  onChange={(next) => void act(() => adminApi.ai.update({ tasks: { [task.key]: next } }))}
                />
              </div>
            </li>
          ))}
        </ul>
      </Section>

      <Section title={t('history')} subtitle={t('aiHistoryHint')} flush>
        {history.length === 0 ? (
          <p className="border-t border-line px-5 py-4 text-sm text-ink-muted">{t('aiNoHistory')}</p>
        ) : (
          <div className="overflow-x-auto border-t border-line">
            <Table>
              <thead>
                <tr>
                  <Th>{t('when')}</Th>
                  <Th>{t('aiTask')}</Th>
                  <Th>{t('aiModelName')}</Th>
                  <Th align="end">{t('aiTokens')}</Th>
                  <Th>{t('result')}</Th>
                </tr>
              </thead>
              <tbody>
                {history.map((row) => (
                  <Tr key={row.id}>
                    <Td className="text-xs whitespace-nowrap text-ink-muted">{dateTime(row.createdAt, locale)}</Td>
                    <Td dir="ltr" className="font-mono text-xs text-ink">
                      {row.task}
                    </Td>
                    <Td dir="ltr" className="text-xs text-ink-muted">
                      {row.model}
                    </Td>
                    <Td align="end" data-numeric className="text-xs text-ink-muted">
                      {row.promptTokens === null && row.completionTokens === null
                        ? '—'
                        : `${(row.promptTokens ?? 0) + (row.completionTokens ?? 0)}`}
                    </Td>
                    <Td className="text-xs">
                      <span className={cx(row.succeeded ? 'text-success' : 'text-danger')}>
                        {row.succeeded ? t('aiOk') : t('aiFailed')}
                      </span>
                      <span data-numeric className="ms-2 text-ink-faint">
                        {row.durationMs}ms
                      </span>
                      {/* The provider's own words: a wrong key and an unknown
                          model are different problems to fix. */}
                      {row.error ? <span className="ms-2 text-ink-muted">{row.error}</span> : null}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Section>
    </SettingsPage>
  );
}
